<template>
  <page-header-wrapper>
    <div class="purchase-inbound-page erp-list-page baidao-list-page">
      <section class="erp-search-card purchase-search">
        <div class="search-fields">
          <a-input v-model.trim="query.inboundNo" allow-clear placeholder="采购入库单编号" @pressEnter="loadData" />
          <a-range-picker v-model="dateRange" value-format="YYYY-MM-DD" style="width:100%" />
          <a-select v-model="query.warehouseId" allow-clear show-search option-filter-prop="children" placeholder="入库仓库">
            <a-select-option v-for="item in warehouses" :key="item.id" :value="item.id">{{ item.warehouseCode }} · {{ item.warehouseName }}</a-select-option>
          </a-select>
          <a-select v-model="query.auditStatus" allow-clear placeholder="审核状态">
            <a-select-option value="unaudited">未审核</a-select-option>
            <a-select-option value="audited">已审核</a-select-option>
          </a-select>
        </div>
        <div class="search-actions">
          <a-button type="primary" @click="search">搜索</a-button>
          <a-button @click="resetQuery">重置</a-button>
        </div>
      </section>

      <section class="erp-table-card">
        <header class="erp-table-toolbar">
          <div>
            <h3>采购入库单</h3>
            <p>必须选择已审核且有剩余数量的采购订单（未审核入库单也会占用剩余）；审核时增加库存并回写订单已入数量。</p>
          </div>
          <a-button type="primary" icon="plus" @click="openCreate">新建采购入库</a-button>
        </header>
        <a-table
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          row-key="id"
          bordered
          size="middle"
          :pagination="pagination"
          :scroll="{ x: 1680 }"
          :locale="{ emptyText: '暂无采购入库单' }"
          @change="onTableChange"
        >
          <template slot="status" slot-scope="value">
            <a-tag :color="value === 'audited' ? 'green' : 'orange'">{{ value === 'audited' ? '已审核' : '未审核' }}</a-tag>
          </template>
          <template slot="amount" slot-scope="value"><span class="money">¥ {{ money(value) }}</span></template>
          <template slot="dash">—</template>
          <template slot="operation" slot-scope="text, record">
            <div class="row-actions">
              <a @click="openView(record)">查看</a>
              <a v-if="record.auditStatus === 'unaudited'" @click="openEdit(record)">编辑</a>
              <a v-if="record.auditStatus === 'unaudited'" @click="confirmAudit(record)">审核</a>
              <a v-if="record.auditStatus === 'audited'" @click="confirmReverse(record)">反审核</a>
              <a v-if="record.auditStatus === 'unaudited'" class="erp-danger-link" @click="confirmDelete(record)">删除</a>
            </div>
          </template>
        </a-table>
      </section>

      <a-drawer
        :width="drawerWidth"
        :visible="drawerVisible"
        :closable="false"
        :mask-closable="false"
        :body-style="{ padding: 0 }"
        class="erp-doc-drawer purchase-inbound-drawer"
        @close="closeDrawer"
      >
        <div class="erp-drawer-shell">
          <div class="erp-drawer-header">
            <div>
              <button class="erp-back-button" type="button" @click="closeDrawer"><a-icon type="arrow-left" /></button>
              <span class="erp-drawer-kicker">{{ readOnly ? '查看采购入库' : (form.id ? '编辑采购入库' : '新建采购入库') }}</span>
              <h3>{{ form.inboundNo || '新建采购入库' }}</h3>
            </div>
            <a-tag :color="form.auditStatus === 'audited' ? 'green' : 'orange'">{{ form.auditStatus === 'audited' ? '已审核' : '未审核' }}</a-tag>
          </div>

          <a-form-model ref="documentForm" :model="form" :rules="rules" layout="vertical" class="document-body erp-drawer-content">
            <section class="erp-form-card">
              <div class="erp-section-title">
                <span class="erp-section-index">01</span>
                <div><h4>基础信息</h4><p>来源只能选择已审核且仍有剩余待入库数量的采购订单。</p></div>
              </div>
              <div class="document-grid">
                <a-form-model-item label="采购入库单编号">
                  <a-input :value="form.inboundNo || '系统自动生成'" disabled />
                </a-form-model-item>
                <a-form-model-item label="来源采购订单" prop="sourceOrderId">
                  <a-select
                    v-model="form.sourceOrderId"
                    show-search
                    option-filter-prop="children"
                    :disabled="readOnly || !!form.id || lockedFromOrder"
                    placeholder="请选择已审核采购订单"
                    style="width:100%"
                    @change="onSourceOrderChange"
                  >
                    <a-select-option v-for="item in orderOptions" :key="item.id" :value="item.id">
                      {{ item.orderNo }} · {{ item.supplierName }}（剩 {{ remainSummary(item) }}）
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
                <a-form-model-item label="入库日期" prop="inboundDate">
                  <a-date-picker v-model="form.inboundDate" value-format="YYYY-MM-DD" :disabled="readOnly" style="width:100%" />
                </a-form-model-item>
                <a-form-model-item label="入库仓库">
                  <a-input :value="warehouseLabel" disabled />
                </a-form-model-item>
                <a-form-model-item label="供应商编号"><a-input :value="form.supplierCode" disabled /></a-form-model-item>
                <a-form-model-item label="供应商名称"><a-input :value="form.supplierName" disabled /></a-form-model-item>
                <a-form-model-item class="grid-span-two" label="备注">
                  <a-input v-model.trim="form.remark" :disabled="readOnly" :max-length="500" />
                </a-form-model-item>
              </div>
            </section>

            <section class="erp-form-card">
              <div class="erp-section-title">
                <span class="erp-section-index">02</span>
                <div><h4>商品明细</h4><p>商品、单位、单价由采购订单带出，仅可修改本次入库数量（不超过剩余可入）。</p></div>
              </div>
              <div class="detail-table-wrap erp-line-table-wrap">
                <table class="detail-table erp-line-table">
                  <thead>
                    <tr>
                      <th style="width:56px">序号</th>
                      <th style="min-width:240px">商品</th>
                      <th style="width:90px">单位</th>
                      <th style="width:120px">剩余可入</th>
                      <th style="width:140px">本次入库数量</th>
                      <th style="width:120px">含税单价</th>
                      <th style="width:120px">金额</th>
                      <th v-if="!readOnly" style="width:70px">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in form.items" :key="item.clientKey || item.id || index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ item.skuCode }} · {{ item.skuName }}</td>
                      <td>{{ item.unitName }}</td>
                      <td>{{ qtyText(item.qtyRemainingBusiness) }}</td>
                      <td>
                        <a-input-number
                          v-model="item.qtyBusiness"
                          :disabled="readOnly"
                          :min="0.001"
                          :max="Number(item.qtyRemainingBusiness || 0) || undefined"
                          :precision="3"
                          style="width:100%"
                        />
                      </td>
                      <td class="money">¥ {{ money(item.purchasePrice) }}</td>
                      <td class="money">¥ {{ lineAmount(item) }}</td>
                      <td v-if="!readOnly"><a class="erp-danger-link" @click="removeLine(index)">移除</a></td>
                    </tr>
                    <tr v-if="!form.items.length">
                      <td :colspan="readOnly ? 7 : 8" class="empty-row">请先选择来源采购订单</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="amount-summary"><span>实际入库金额</span><b>¥ {{ totalAmount }}</b></div>
            </section>
          </a-form-model>

          <div v-if="drawerVisible" class="erp-drawer-footer">
            <div><a-icon type="info-circle" /> 分批入库允许，单次数量不可超过订单剩余；审核后可反审核冲回库存。</div>
            <div>
              <a-button @click="closeDrawer">取消</a-button>
              <template v-if="!readOnly">
                <a-button :loading="saving" @click="submit(false)">保存</a-button>
                <a-button type="primary" :loading="saving" @click="submit(true)">保存并审核</a-button>
              </template>
            </div>
          </div>
        </div>
      </a-drawer>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import { listWarehouse } from '@/api/erp/warehouse'
import { listPurchaseOrderInboundOptions, getPurchaseOrder } from '@/api/erp/purchaseOrder'
import {
  pagePurchaseInbound, getPurchaseInbound, savePurchaseInbound,
  auditPurchaseInbound, reverseAuditPurchaseInbound, deletePurchaseInbound
} from '@/api/erp/purchaseInbound'

const emptyForm = () => ({
  id: undefined,
  inboundNo: '',
  inboundDate: moment().format('YYYY-MM-DD'),
  warehouseId: undefined,
  warehouseName: '',
  supplierCode: '',
  supplierName: '',
  sourceOrderId: undefined,
  sourceOrderNo: '',
  remark: '',
  auditStatus: 'unaudited',
  stockPostStatus: 'unposted',
  items: []
})

export default {
  name: 'ErpPurchaseInbound',
  data () {
    return {
      viewportWidth: window.innerWidth,
      loading: false,
      saving: false,
      drawerVisible: false,
      readOnly: false,
      lockedFromOrder: false,
      query: {},
      dateRange: [],
      rows: [],
      warehouses: [],
      orderOptions: [],
      form: emptyForm(),
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showQuickJumper: true },
      rules: {
        inboundDate: [{ required: true, message: '请选择入库日期', trigger: 'change' }],
        sourceOrderId: [{ required: true, message: '请选择来源采购订单', trigger: 'change' }]
      },
      columns: [
        { title: '采购入库单编号', dataIndex: 'inboundNo', width: 200 },
        { title: '审核状态', dataIndex: 'auditStatus', width: 90, scopedSlots: { customRender: 'status' } },
        { title: '供应商名称', dataIndex: 'supplierName', width: 160 },
        { title: '入库日期', dataIndex: 'inboundDate', width: 110 },
        { title: '入库仓库', dataIndex: 'warehouseName', width: 130 },
        { title: '采购订单编号', dataIndex: 'sourceOrderNo', width: 180 },
        { title: '实际入库金额', dataIndex: 'totalAmount', width: 120, align: 'right', scopedSlots: { customRender: 'amount' } },
        { title: '已结算金额', key: 'settled', width: 110, align: 'right', scopedSlots: { customRender: 'dash' } },
        { title: '未结算金额', key: 'unsettle', width: 110, align: 'right', scopedSlots: { customRender: 'dash' } },
        { title: '验收人', key: 'accepter', width: 100, scopedSlots: { customRender: 'dash' } },
        { title: '备注', dataIndex: 'remark', width: 140, ellipsis: true },
        { title: '操作', key: 'operation', width: 220, fixed: 'right', scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  computed: {
    drawerWidth () { return this.viewportWidth < 1100 ? '100%' : '88%' },
    totalAmount () {
      return this.form.items.reduce((sum, item) => sum + Number(item.qtyBusiness || 0) * Number(item.purchasePrice || 0), 0).toFixed(2)
    },
    warehouseLabel () {
      if (!this.form.warehouseId) return ''
      const found = this.warehouses.find(item => String(item.id) === String(this.form.warehouseId))
      if (found) return `${found.warehouseCode} · ${found.warehouseName}`
      return this.form.warehouseName || ''
    }
  },
  created () {
    this.loadBaseData()
    this.loadData()
    this.tryOpenFromOrder()
  },
  activated () {
    this.tryOpenFromOrder()
  },
  watch: {
    '$route.query.fromOrderId' (val) {
      if (val) this.tryOpenFromOrder()
    }
  },
  mounted () { window.addEventListener('resize', this.onResize) },
  beforeDestroy () { window.removeEventListener('resize', this.onResize) },
  methods: {
    onResize () { this.viewportWidth = window.innerWidth },
    loadBaseData () {
      listWarehouse().then(res => { this.warehouses = res.data || [] })
    },
    loadOrderOptions () {
      return listPurchaseOrderInboundOptions().then(res => {
        this.orderOptions = res.data || []
      }).catch(() => {
        this.orderOptions = this.orderOptions || []
      })
    },
    tryOpenFromOrder () {
      const fromQuery = this.$route.query.fromOrderId
      const fromStorage = sessionStorage.getItem('purchaseInboundFromOrderId')
      const orderId = fromQuery || fromStorage
      if (!orderId) return
      if (this._openingFromOrderId === String(orderId)) return
      this._openingFromOrderId = String(orderId)
      sessionStorage.removeItem('purchaseInboundFromOrderId')
      this.loadOrderOptions().finally(() => {
        this.openCreateFromOrder(String(orderId)).finally(() => {
          this._openingFromOrderId = ''
        })
      })
    },
    loadData () {
      this.loading = true
      const params = {
        ...this.query,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize,
        dateStart: this.dateRange[0],
        dateEnd: this.dateRange[1]
      }
      pagePurchaseInbound(params).then(res => {
        const page = res.data || {}
        this.rows = page.records || []
        this.pagination.total = Number(page.total || 0)
      }).finally(() => { this.loading = false })
    },
    search () { this.pagination.current = 1; this.loadData() },
    resetQuery () { this.query = {}; this.dateRange = []; this.pagination.current = 1; this.loadData() },
    onTableChange (page) { this.pagination.current = page.current; this.pagination.pageSize = page.pageSize; this.loadData() },
    remainSummary (order) {
      const items = order.items || []
      const total = items.reduce((sum, item) => sum + Number(item.qtyRemainingBusiness || 0), 0)
      return total.toFixed(2)
    },
    qtyText (value) { return Number(value || 0).toFixed(3).replace(/\.?0+$/, '') || '0' },
    mapOrderItems (items) {
      return (items || [])
        .filter(item => Number(item.qtyRemainingBusiness || item.qtyRemaining || 0) > 0)
        .map(item => ({
          clientKey: `inbound-line-${item.id}`,
          sourceOrderItemId: item.id,
          skuId: item.skuId,
          skuCode: item.skuCode,
          skuName: item.skuName,
          unitId: item.unitId,
          unitName: item.unitName,
          qtyRemainingBusiness: item.qtyRemainingBusiness != null
            ? item.qtyRemainingBusiness
            : item.qtyRemaining,
          qtyBusiness: item.qtyRemainingBusiness != null
            ? item.qtyRemainingBusiness
            : item.qtyRemaining,
          purchasePrice: item.purchasePrice
        }))
    },
    applyOrder (order) {
      this.form.sourceOrderId = order.id
      this.form.sourceOrderNo = order.orderNo
      this.form.warehouseId = order.warehouseId
      this.form.warehouseName = order.warehouseName
      this.form.supplierCode = order.supplierCode
      this.form.supplierName = order.supplierName
      this.form.items = this.mapOrderItems(order.items)
    },
    ensureOrderOption (order) {
      if (!order || order.id == null) return
      const idx = this.orderOptions.findIndex(item => String(item.id) === String(order.id))
      if (idx >= 0) {
        this.$set(this.orderOptions, idx, { ...this.orderOptions[idx], ...order })
      } else {
        this.orderOptions = [order, ...this.orderOptions]
      }
    },
    openCreate () {
      this.lockedFromOrder = false
      this.form = emptyForm()
      this.readOnly = false
      this.loadOrderOptions()
      this.drawerVisible = true
    },
    openCreateFromOrder (orderId) {
      const id = orderId != null ? String(orderId) : ''
      if (!id) return Promise.resolve()
      const clearQuery = () => {
        if (this.$route.query.fromOrderId) {
          this.$router.replace({ path: this.$route.path || '/erp/purchase-inbound', query: {} })
        }
      }
      const apply = (order) => {
        this.ensureOrderOption(order)
        this.lockedFromOrder = true
        this.form = emptyForm()
        this.applyOrder(order)
        this.readOnly = false
        if (!this.form.items.length) {
          this.$message.warning('该采购订单已无剩余待入库数量')
        }
        this.drawerVisible = true
        clearQuery()
      }
      const cached = this.orderOptions.find(item => String(item.id) === id)
      if (cached && (cached.items || []).length) {
        apply(cached)
        return Promise.resolve()
      }
      return getPurchaseOrder(id).then(res => {
        const order = res.data || {}
        if (order.auditStatus !== 'audited') {
          this.$message.warning('只能从已审核的采购订单生成入库单')
          clearQuery()
          return
        }
        apply(order)
      }).catch(() => {
        this.$message.error('加载采购订单失败')
        clearQuery()
      })
    },
    onSourceOrderChange (orderId) {
      const order = this.orderOptions.find(item => String(item.id) === String(orderId))
      if (!order) return
      this.applyOrder(order)
    },
    openEdit (record) { this.openDocument(record.id, false) },
    openView (record) { this.openDocument(record.id, true) },
    openDocument (id, readOnly) {
      getPurchaseInbound(id).then(res => {
        const data = res.data || {}
        this.lockedFromOrder = false
        this.form = {
          ...emptyForm(),
          ...data,
          items: (data.items || []).map(item => ({
            ...item,
            clientKey: `inbound-line-${item.id}`,
            qtyRemainingBusiness: item.qtyRemainingBusiness != null
              ? item.qtyRemainingBusiness
              : item.qtyBusiness
          }))
        }
        this.readOnly = readOnly || data.auditStatus === 'audited'
        this.drawerVisible = true
      })
    },
    closeDrawer () { if (!this.saving) this.drawerVisible = false },
    removeLine (index) { this.form.items.splice(index, 1) },
    lineAmount (item) {
      return (Number(item.qtyBusiness || 0) * Number(item.purchasePrice || 0)).toFixed(2)
    },
    validateItems () {
      if (!this.form.sourceOrderId) return '请选择来源采购订单'
      if (!this.form.items.length) return '请至少保留一条待入库明细'
      if (this.form.items.some(item => !item.sourceOrderItemId)) return '明细缺少来源订单行'
      if (this.form.items.some(item => Number(item.qtyBusiness) <= 0)) return '本次入库数量必须大于0'
      if (this.form.items.some(item => Number(item.qtyBusiness) > Number(item.qtyRemainingBusiness || 0) + 1e-9)) {
        return '本次入库数量不能超过剩余可入数量'
      }
      return ''
    },
    submit (auditAfterSave) {
      this.$refs.documentForm.validate(valid => {
        if (!valid) return
        const itemError = this.validateItems()
        if (itemError) return this.$message.warning(itemError)
        this.saving = true
        const payload = {
          id: this.form.id,
          inboundDate: this.form.inboundDate,
          warehouseId: this.form.warehouseId,
          sourceOrderId: this.form.sourceOrderId,
          remark: this.form.remark,
          items: this.form.items.map(item => ({
            sourceOrderItemId: item.sourceOrderItemId,
            skuId: item.skuId,
            unitId: item.unitId,
            qtyBusiness: item.qtyBusiness
          }))
        }
        savePurchaseInbound(payload).then(res => {
          if (!auditAfterSave) {
            this.$message.success('采购入库单已保存')
            this.drawerVisible = false
            this.loadData()
            this.loadOrderOptions()
            return null
          }
          return auditPurchaseInbound(res.data.id).then(refresh => {
            const result = refresh.data || {}
            this.$message.success(`审核完成，已刷新 ${result.guidePriceCount || 0} 条价格`)
            this.drawerVisible = false
            this.loadData()
            this.loadOrderOptions()
          })
        }).finally(() => { this.saving = false })
      })
    },
    confirmAudit (record) {
      this.$confirm({
        title: '审核采购入库单？',
        content: '审核后将增加库存，并回写采购订单已入库数量。',
        onOk: () => auditPurchaseInbound(record.id).then(res => {
          const result = res.data || {}
          this.$message.success(`审核完成，已刷新 ${result.guidePriceCount || 0} 条价格`)
          this.loadData()
          this.loadOrderOptions()
        })
      })
    },
    confirmReverse (record) {
      this.$confirm({
        title: '反审核采购入库单？',
        content: '将冲回库存并恢复采购订单剩余可入数量。',
        onOk: () => reverseAuditPurchaseInbound(record.id).then(() => {
          this.$message.success('已反审核')
          this.loadData()
          this.loadOrderOptions()
        })
      })
    },
    confirmDelete (record) {
      this.$confirm({
        title: '删除未审核采购入库单？',
        content: record.inboundNo,
        okType: 'danger',
        onOk: () => deletePurchaseInbound(record.id).then(() => {
          this.$message.success('已删除')
          this.loadData()
          this.loadOrderOptions()
        })
      })
    },
    money (value) { return Number(value || 0).toFixed(2) }
  }
}
</script>

<style scoped>
.purchase-inbound-page{min-height:680px}
.purchase-search{display:flex;align-items:center;justify-content:space-between;gap:16px}
.search-fields{display:grid;grid-template-columns:220px 260px 230px 140px;gap:10px;flex:1}
.search-actions{display:flex;gap:8px}
.row-actions{display:flex;flex-wrap:wrap;gap:10px}
.money{font-variant-numeric:tabular-nums}
.document-body{padding:16px 24px}
.document-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:0 14px}
.grid-span-two{grid-column:span 2}
.detail-table{min-width:980px}
.empty-row{text-align:center;color:#7b8798;padding:28px!important}
.amount-summary{display:flex;justify-content:flex-end;gap:18px;padding-top:12px;align-items:baseline}
.amount-summary span{color:#7b8798}
.amount-summary b{font-size:22px;color:#26364b}
@media(max-width:1100px){.purchase-search{flex-direction:column;align-items:stretch}.search-fields,.document-grid{grid-template-columns:1fr 1fr}.grid-span-two{grid-column:span 1}}
</style>
